import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VoiceFeatureSettings } from '../../../../shared-types/src/realtime.js';
import type { AdminHttpClient } from '../../api/client.js';
import { describeError } from '../../errors.js';
import { TenantVoicePanel } from './TenantVoicePanel.js';

// The Platform scope summary already shows the no-tenant hint for this tab.
// The global card sits outside the tenant key, so tenant changes or failures never reset it.
export function VoiceSettings({ http, tenantId }: { http: AdminHttpClient; tenantId: string }) {
  return <>
    <VoiceFeatureSettingsPanel http={http} />
    {tenantId ? <TenantVoicePanel key={tenantId} http={http} tenantId={tenantId} /> : null}
  </>;
}

/** The platform master for every voice kind. Saving it never touches models, rates, assignments or tenant policy. */
export function VoiceFeatureSettingsPanel({ http }: { http: AdminHttpClient }) {
  const { t } = useTranslation();
  const [saved, setSaved] = useState<VoiceFeatureSettings>();
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState(false);
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  const pending = useRef(false);

  useEffect(() => {
    const current = ++generation.current;
    pending.current = true;
    setBusy(true);
    setSaved(undefined);
    setError(undefined);
    setSuccess(false);
    void http.getVoiceSettings().then(next => {
      if (generation.current !== current) return;
      setSaved(next);
      setEnabled(next.enabled);
    }).catch(cause => {
      if (generation.current === current) setError(describeError(cause, t, 'voiceFeature.loadFailed'));
    }).finally(() => {
      if (generation.current === current) { pending.current = false; setBusy(false); }
    });
    return () => { generation.current = current + 1; };
  }, [http, reload, t]);

  async function save() {
    if (pending.current || !saved || saved.enabled === enabled) return;
    pending.current = true;
    const current = generation.current;
    setBusy(true);
    setError(undefined);
    setSuccess(false);
    try {
      const next = await http.putVoiceSettings({ enabled, expectedFeatureRevision: saved.revision });
      if (generation.current !== current) return;
      setSaved(next);
      setEnabled(next.enabled);
      setSuccess(true);
    } catch (cause) {
      if (generation.current === current) setError(describeError(cause, t, 'voiceFeature.saveFailed'));
    } finally {
      if (generation.current === current) { pending.current = false; setBusy(false); }
    }
  }

  return <section className="panel realtime-voice-feature" aria-label={t('voiceFeature.title')} aria-busy={busy}>
    <h3>{t('voiceFeature.title')}</h3>
    <p className="platform-scope-badge">{t('voiceFeature.scope')}</p>
    <p className="screen-help">{t('voiceFeature.description')}</p>
    <p>{saved ? t(saved.enabled ? 'voiceFeature.on' : 'voiceFeature.off')
      : t(busy ? 'voiceFeature.loading' : 'voiceFeature.unknown')}</p>
    <fieldset disabled={busy || !saved}>
      <label className="check-row">
        <input type="checkbox" checked={enabled} onChange={event => { setEnabled(event.target.checked); setSuccess(false); }} />
        {t('voiceFeature.enable')}
      </label>
      <button type="button" disabled={enabled === saved?.enabled} onClick={() => void save()}>{t('voiceFeature.save')}</button>
    </fieldset>
    {success ? <p role="status">{t('voiceFeature.saved')}</p> : null}
    {error ? <div role="alert"><p>{error}</p>
      <button type="button" className="secondary" disabled={busy} onClick={() => setReload(value => value + 1)}>{t('voiceFeature.retry')}</button>
    </div> : null}
  </section>;
}
